import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLinkActive, RouterModule } from '@angular/router';
import { hasActiveDescendant } from 'app/core/customer-menus/menu-active.util';
import { MenuItem } from 'app/core/models/menu/menu.model';
import { NavActiveUrlService } from 'app/core/services/menu/nav-active-url.service';

@Component({
  selector: 'sqep-menu-item',
  imports: [RouterModule, RouterLinkActive, MatIconModule, MenuItemComponent],
  templateUrl: './menu-item.component.html',
  styleUrl: './menu-item.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('expandContractMenu', [
      transition(':enter', [
        style({ opacity: 0, height: '0px' }),
        animate('500ms ease-in-out', style({ opacity: 1, height: '*' })),
      ]),
      transition(':leave', [
        animate('500ms ease-in-out', style({ opacity: 0, height: '0px' })),
      ]),
    ]),
  ],
})
export class MenuItemComponent {
  protected readonly item = input.required<MenuItem>();
  protected readonly depth = input<number>(0);
  protected readonly isViewingOther = input<boolean>(false);
  protected readonly expanded = input<boolean>(false);
  protected readonly inFlyout = input<boolean>(false);
  protected readonly isLastChild = input<boolean>(false);
  protected readonly toggled = output<void>();
  protected readonly navigated = output<void>();

  private readonly navActiveUrl = inject(NavActiveUrlService);
  private readonly currentUrl = this.navActiveUrl.currentUrl;

  protected readonly paddingLeft = computed(() =>
    this.inFlyout() ? this.depth() * 16 : 16 + this.depth() * 16,
  );
  protected readonly paddingRight = computed(() =>
    this.inFlyout() ? this.paddingLeft() : 16,
  );

  protected readonly openChild = signal<MenuItem | null>(null);

  // Memoized so repeated template reads only recompute when this item or
  // the shared URL actually change, rather than on every change-detection pass.
  protected readonly isActiveDescendant = computed(() =>
    hasActiveDescendant(this.item(), this.currentUrl()),
  );

  constructor() {
    effect(() => {
      const url = this.currentUrl();
      this.openChild.set(
        this.item().subMenu?.find((child) => hasActiveDescendant(child, url)) ??
          null,
      );
    });
  }

  protected toggle(): void {
    this.toggled.emit();
  }

  protected onChildToggled(child: MenuItem): void {
    this.openChild.set(this.openChild() === child ? null : child);
  }

  protected onLeafClick(): void {
    this.navigated.emit();
  }

  protected onChildNavigated(): void {
    this.navigated.emit();
  }
}
